import * as React from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { useStoreApplePayStatus } from '@/lib/api/apple-pay-store-status';
import {
  useSuggestPlatformApplePayDomain,
  useEnablePlatformApplePay,
  type EnableApplePayResult,
} from '@/lib/api/apple-pay-registrations';

const SOURCE_LABEL: Record<string, string> = {
  byok_own: 'Your certificates',
  byok_platform: 'Platform (shared) certificate',
  platform_ppiid: 'Tagada platform integrator',
  platform_pool: 'Platform Apple Pay',
};

const STEP_LABEL: Record<string, string> = {
  add: 'Add domain',
  probe: 'Check file',
  register: 'Register with Apple',
  activate: 'Go live',
};

/**
 * Domain status + self-serve enable (TAG-2036), rendered inside the standard
 * "Configuration" card ahead of the regular Apple Pay settings (country,
 * express checkout) — not a separate card. The header switch already handles
 * the common case (auto-resolved domain); this section is what's actually
 * needed when that fails: a place to see why, and to type the right domain.
 *
 * Once live, this collapses to just the domain list — same as every other
 * integration hides its "connect" UI once connected and shows only settings.
 */
export function ApplePayDomainSection({
  storeId,
  onEnabled,
}: {
  storeId: string;
  /** Lets the header switch (which owns `isEnabled`) reflect a successful enable from here. */
  onEnabled: () => void;
}) {
  const status = useStoreApplePayStatus(storeId);
  const isLive = !!status.data && status.data.domains.length > 0 && status.data.integrationEnabled;

  const suggestion = useSuggestPlatformApplePayDomain(storeId, { enabled: !isLive });
  const enable = useEnablePlatformApplePay();
  const [domain, setDomain] = React.useState('');
  const [result, setResult] = React.useState<EnableApplePayResult | null>(null);

  // Only overwrite the field with the suggestion before the merchant has
  // typed anything — never clobber an in-progress edit on refetch.
  const prefilled = React.useRef(false);
  if (!prefilled.current && suggestion.data) {
    prefilled.current = true;
    setDomain(suggestion.data.domain ?? '');
  }

  const handleEnable = async () => {
    setResult(null);
    const outcome = await enable.mutateAsync({ storeId, domain: domain || undefined });
    setResult(outcome);
    if (outcome.steps.every((s) => s.ok)) onEnabled();
  };

  if (status.isLoading) return <Skeleton className="h-16 w-full" />;

  return (
    <div className="space-y-4">
      {status.data && status.data.domains.length > 0 && (
        <ul className="space-y-2">
          {status.data.domains.map((d) => (
            <li key={d.domain} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <code className="text-sm">{d.domain}</code>
                {d.displayName && <span className="text-muted-foreground text-xs">· sheet: {d.displayName}</span>}
              </div>
              <Badge variant="secondary">{SOURCE_LABEL[d.source] ?? d.source}</Badge>
            </li>
          ))}
        </ul>
      )}

      {!isLive && (
        <div className="space-y-3 rounded-md border p-4">
          <div className="space-y-1.5">
            <Label htmlFor="apple-pay-domain">Checkout domain</Label>
            {suggestion.isLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Input
                id="apple-pay-domain"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="checkout.example.com"
                disabled={enable.isPending}
              />
            )}
            {suggestion.data?.reason && <p className="text-muted-foreground text-xs">{suggestion.data.reason}</p>}
          </div>

          <Button type="button" size="sm" onClick={handleEnable} disabled={enable.isPending || !domain}>
            {enable.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {enable.isPending ? 'Enabling…' : 'Enable Apple Pay'}
          </Button>

          {result && (
            <ul className="space-y-1.5 pt-1">
              {result.steps.map((step) => (
                <li key={step.step} className="flex items-center gap-2 text-sm">
                  {step.ok ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                  ) : (
                    <XCircle className="text-destructive h-4 w-4 shrink-0" />
                  )}
                  <span className="font-medium">{STEP_LABEL[step.step] ?? step.step}</span>
                  {step.message && <span className="text-muted-foreground">— {step.message}</span>}
                </li>
              ))}
            </ul>
          )}

          {result && !result.steps.every((s) => s.ok) && (
            <Alert variant="destructive">
              <AlertDescription>
                Stopped partway through — fix the issue above and hit enable again; steps already done won&apos;t
                be repeated.
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}
